import React, { useState } from 'react'
import { IMAGES } from '../data/siteData'
import { getOptimizedImageUrl } from '../lib/imageHelper'

export default function FaqAccordion({
  items = [],
  title = 'Ofte stillede spørgsmål',
  subtitle = 'Find svar på de mest almindelige spørgsmål om arbejdet og processen',
  image = null,
  imageAlt = null
}) {
  const [openIndex, setOpenIndex] = useState(0) // First open by default

  if (!items || items.length === 0) return null

  function toggleIndex(idx) {
    setOpenIndex(openIndex === idx ? null : idx)
  }

  const displayImage = image || IMAGES.belaegning?.hero || IMAGES.hero
  const computedAlt = imageAlt || `${title} - JT Knudsen entreprenørarbejde og haveanlæg`

  return (
    <section className="py-20 sm:py-28 bg-stone-950 text-white border-y border-stone-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
          {/* Left Column: Flush-edged image filling container */}
          <div className="lg:col-span-5 relative w-full min-h-[340px] sm:min-h-[440px] lg:min-h-full">
            <div className="rounded-2xl overflow-hidden shadow-lg border border-stone-800 w-full h-full">
              <img
                src={getOptimizedImageUrl(displayImage, { width: 1000, quality: 80 })}
                alt={computedAlt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          {/* Right Column: FAQ Content */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="mb-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-4 text-base sm:text-lg text-stone-400 italic leading-relaxed">
                  {subtitle}
                </p>
              )}
            </div>

            <div className="border-t border-stone-800">
              {items.map((item, idx) => {
                const isOpen = openIndex === idx
                return (
                  <div
                    key={idx}
                    className="border-b border-stone-800 transition-colors duration-200"
                  >
                    <button
                      type="button"
                      onClick={() => toggleIndex(idx)}
                      className="w-full text-left py-5 flex items-center justify-between gap-4 cursor-pointer focus:outline-none group"
                      aria-expanded={isOpen}
                    >
                      <span className="font-bold text-stone-100 text-base sm:text-lg group-hover:text-emerald-400 transition-colors">
                        {item.question}
                      </span>
                      <div
                        className={`w-6 h-6 flex items-center justify-center shrink-0 transition-transform duration-300 ease-in-out ${
                          isOpen ? 'rotate-180 text-emerald-400' : 'rotate-0 text-stone-500 group-hover:text-stone-300'
                        }`}
                      >
                        <i className="fa-solid fa-chevron-down text-sm" aria-hidden="true"></i>
                      </div>
                    </button>

                    {/* Smooth accordion expansion transition */}
                    <div
                      className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                        isOpen
                          ? 'grid-rows-[1fr] opacity-100'
                          : 'grid-rows-[0fr] opacity-0 pointer-events-none'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="pl-0 sm:pl-2 pr-6 pb-6 pt-1 text-sm sm:text-base text-stone-300 leading-relaxed">
                          <p>{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
